import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { WhatsappShareButton } from 'react-share';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import analytics from '../../../../utils/analytics';
import { PlrButton } from '../landing/ui';
import PlrModal from '../landing/PlrModal';
import { plr } from '../landing/tokens';
import {
  COMPLEMENT_DISCLOSURE, SHARE_BODY, SHARE_TEXT, SHARE_TITLE,
} from './copy';

type ShareDialogProps = {
  open: boolean;
  url: string;
  expiresAt: string;
  onClose: () => void;
  pageCall?: {
    category: string;
    name: string;
    props?: Record<string, string | undefined>;
  };
};

const expiryLine = (expiresAt: string) => {
  const at = new Date(expiresAt);
  if (Number.isNaN(at.getTime())) return 'El enlace vence en 72 horas.';
  return `El enlace vence el ${at.toLocaleDateString('es-CL', {
    day: 'numeric', month: 'long',
  })} a las ${at.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}.`;
};

const channelSx = {
  flex: 1,
  minWidth: 0,
  height: 48,
  boxSizing: 'border-box',
  border: `1px solid ${plr.border}`,
  borderRadius: '16px',
  background: '#FFFFFF',
  color: plr.ink,
  fontFamily: plr.font,
  fontSize: 14,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  textDecoration: 'none',
  transition: 'border-color .15s',
  '&:hover': { borderColor: plr.inkHover, color: plr.ink },
  '& svg': { fontSize: 19 },
} as const;

const MAIL_SUBJECT = 'Postula a un crédito con banca.me';

const trackChannel = (channel: string) => {
  analytics.track('Click Boton', { text: `Compartir mediante ${channel}`, initiator: 'usuario' });
};

// Paso "compartir" del complemento (4f/4fb), con el mismo modal que el resto del flujo:
// hoja inferior en mobile, diálogo centrado en desktop; se cierra con la X del título.
export default function ShareDialog({
  open, url, expiresAt, onClose, pageCall,
}: ShareDialogProps) {
  const [copied, setCopied] = React.useState(false);
  const [copyError, setCopyError] = React.useState(false);

  React.useEffect(() => {
    if (open && pageCall) analytics.page(pageCall.category, pageCall.name, pageCall.props);
  }, [open]);

  const copy = async () => {
    analytics.track('Click Boton', { text: 'Copiar enlace', initiator: 'usuario' });
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyError(false);
    } catch (e) {
      setCopyError(true);
    }
  };

  // Correo como link mailto: normal. EmailShareButton (react-share) abre el correo en su onClick
  // por defecto, y al pasarle un onClick para el evento se perdía: el botón no hacía nada.
  const mailHref = `mailto:?subject=${encodeURIComponent(MAIL_SUBJECT)}&body=${encodeURIComponent(`${SHARE_TEXT} ${url}`)}`;

  return (
    <PlrModal
      open={open}
      onClose={onClose}
      title={SHARE_TITLE}
      titleId="complement-share-title"
      testId="complement-share"
      width={460}
    >
      <Box
        component="p"
        sx={{
          m: '-6px 0 0', fontSize: { xs: 13.5, sm: 14 }, lineHeight: 1.45, color: plr.body,
        }}
      >
        {SHARE_BODY}
      </Box>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        p: '6px 6px 6px 14px',
        border: `1px solid ${plr.border}`,
        borderRadius: '18px',
        background: plr.surface,
      }}
      >
        <Box sx={{ color: plr.muted, display: 'flex' }}><LinkIcon sx={{ fontSize: 19 }} /></Box>
        <Box
          component="span"
          data-pw="complement-share-url"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 13.5,
            color: plr.body,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {url.replace(/^https?:\/\//, '')}
        </Box>
        <PlrButton
          testId="complement-share-copy"
          onClick={copy}
          sx={{
            height: 44, px: '18px', fontSize: 15, flexShrink: 0,
          }}
        >
          {copied && <CheckIcon sx={{ fontSize: 18 }} />}
          <span>{copied ? '¡Copiado!' : 'Copiar'}</span>
        </PlrButton>
      </Box>
      {copyError && (
        <Box component="p" role="alert" sx={{ m: '-6px 0 0', fontSize: 13, color: '#C0392B' }}>
          No pudimos copiar el enlace. Compártelo con una de las opciones de abajo.
        </Box>
      )}
      <Box
        component="span"
        sx={{
          fontSize: 13, fontWeight: 700, color: plr.muted, mb: '-6px',
        }}
      >
        O envíalo por
      </Box>
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <WhatsappShareButton
          url={url}
          title={SHARE_TEXT}
          onClick={() => trackChannel('Whatsapp')}
          style={{ flex: 1, minWidth: 0, display: 'flex' }}
        >
          <Box component="span" sx={{ ...channelSx, width: '100%', px: '10px' }}>
            <WhatsAppIcon />
            WhatsApp
          </Box>
        </WhatsappShareButton>
        {/* Sin SMS (Max, 2 nov): un SMS con un link parece phishing. */}
        <ButtonBase
          component="a"
          href={mailHref}
          data-pw="complement-share-email"
          onClick={() => trackChannel('Email')}
          sx={channelSx}
        >
          <MailOutlineIcon />
          Correo
        </ButtonBase>
      </Box>
      <Box component="span" sx={{ fontSize: 13, color: plr.body }}>
        {expiryLine(expiresAt)}
      </Box>
      <Box
        component="p"
        sx={{
          m: 0, fontSize: { xs: 11.5, sm: 12 }, lineHeight: 1.45, color: plr.muted,
        }}
      >
        {COMPLEMENT_DISCLOSURE}
      </Box>
    </PlrModal>
  );
}

ShareDialog.defaultProps = {
  pageCall: undefined,
};
